import { useRouter, useRuntimeConfig } from '#imports'
import type { RouteLocationAsRelativeGeneric } from 'vue-router'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { clearAuthStorages } from '../utils/clearAuthStorage'

import { useAuthOrigin } from './useAuthOrigin'
import { useRedirectLocale } from './useRedirectLocale'
import { useUserStore } from './useUserStore'

/*
 * Log the current user out.
 *
 * This functions takes care of logging people out from everywhere (Badoom,
 * the Identity stack, etc), and clears the different internal storages. In
 * the end, people get redirect to the default location given as part of
 * the module configuration.
 *
 * @example
 * const { logout } = useLogout()
 *
 * logout() // Call this whenever you want.
 */
export function useLogout() {
  const router = useRouter()
  const config = useRuntimeConfig().public.oauth
  const $httpFetch = useHttpFetch()
  const { $reset } = useUserStore()

  async function logout(redirection?: RouteLocationAsRelativeGeneric) {
    await $httpFetch('/bm/client/logout', {
      method: 'POST',
    })
    await $httpFetch('/auth/logout', { baseURL: useAuthOrigin() })

    $reset()

    await clearAuthStorages()

    const { getRouteLocationWithLocale } = useRedirectLocale()

    // Force a reload to clear the in-memory data (stores, components, etc).
    const redirect = router.resolve(
      redirection ||
        getRouteLocationWithLocale(
          config.defaultRedirectLocationWhenUnauthenticated,
        ),
    )
    window.location.assign(redirect.href)
  }

  return { logout }
}
